import React, { useEffect } from 'react';
import './App.scss';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { Box, Theme, ThemeProvider } from '@mui/material';
import HomePage from './components/HomePage/HomePage';
import SizeMatchComponent from './components/sizeMatch/SizeMatchComponent/SizeMatchComponent';
import MeasurementsComponent from './components/MeasurementsComponent/MeasurementsComponent';
import boddTheme, {
    portwestTheme,
    riyadhAirTheme,
    towerTheme,
    cooneenTheme,
    nugentTheme,
    leidosTheme,
    boddDemoTheme,
    stLogisticsTheme,
    britishAirwaysTheme,
    gardaTheme,
    emiratesTheme,
} from './theme/theme';
import WellnessComponent from './components/wellness/WellnessComponent/WellnessComponent';
import NavigationPage from './components/NavigationPage/NavigationPage';
import ErrorComponent from './components/ErrorComponent/ErrorComponent';
import PageSkeleton from './components/PageSkeleton/PageSkeleton';
import BodyCompositionComponent from './components/bodyComposition/BodyCompositionComponent/BodyCompositionComponent';
import useSessionData from './hooks/useSessionData';
import { getSampleBodyComposition, getSampleWellnessData } from './services/sampleDataService';

const getTheme = (merchantName: string | undefined): Theme => {
    switch (merchantName) {
        case 'Riyadh Air':
            return riyadhAirTheme;
        case 'Portwest':
            return portwestTheme;
        case 'Tower':
            return towerTheme;
        case 'Cooneen':
            return cooneenTheme;
        case 'Nugent':
            return nugentTheme;
        case 'Leidos':
            return leidosTheme;
        case 'Bodd AWS':
            return boddDemoTheme;
        case 'ST Logistics':
            return stLogisticsTheme;
        case 'British Airways':
            return britishAirwaysTheme;
        case 'Garda':
            return gardaTheme;
        case 'Emirates':
            return emiratesTheme;
        default:
            return boddTheme;
    }
};

function App() {
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const sessionId = searchParams.get('sessionId');
    const region = searchParams.get('region');

    if (sessionId === null) {
        return (
            <ErrorComponent title="No session Id" message="SessionId is expected in the query string" />
        );
    }

    const {
        isError,
        error,
        isLoading,
        isScanPending,
        measurements,
        hasMeasurements,
        wellnessData,
        hasWellness,
        sizeMatchRecommendations,
        hasSizeMatch,
        bodyComposition,
        hasBodyComposition,
        merchantName,
        frontProfileImageUrl,
        sideProfileImageUrl,
    } = useSessionData(sessionId, region);

    // Scroll restoration on routing
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    const theme = getTheme(merchantName);

    const showWellnessSampleData = theme.sampleData?.wellness?.enabled ?? false;
    const showBodyCompositionSampleData = theme.sampleData?.bodyComposition?.enabled ?? false;

    if (isError) {
        return (
            <ErrorComponent title="An error has occured" message={error?.message} />
        );
    }

    if (isLoading || isScanPending) {
        return (
            <div>
                <PageSkeleton />
            </div>
        );
    }

    return (
        <Box
            className="App"
            data-testid="App"
            sx={{ alignContent: 'stretch', flexDirection: 'column', width: '100%', alignItems: 'flex-start' }}
        >
            <ThemeProvider theme={theme}>
                <Routes>
                    <Route
                        path="/"
                        element={(
                            <HomePage
                                hasSizeMatch={hasSizeMatch}
                                hasMeasurements={hasMeasurements}
                                hasWellness={hasWellness}
                                hasBodyComposition={hasBodyComposition}
                                frontProfileImageUrl={frontProfileImageUrl}
                                sideProfileImageUrl={sideProfileImageUrl}
                            />
                        )}
                    />
                    {hasSizeMatch && (
                        <Route
                            path="/sizematch"
                            element={(
                                <NavigationPage alertType="sizeMatch">
                                    <SizeMatchComponent
                                        sizeMatchRecommendations={sizeMatchRecommendations}
                                    />
                                </NavigationPage>
                            )}
                        />
                    )}
                    {
                        hasMeasurements && (
                            <Route
                                path="/measurements"
                                element={(
                                    <NavigationPage alertType="measurements">
                                        <MeasurementsComponent
                                            measurements={measurements}
                                        />
                                    </NavigationPage>
                                )}
                            />
                        )
                    }
                    {
                        (hasWellness || showWellnessSampleData) && (
                            <Route
                                path="/wellness"
                                element={(
                                    <NavigationPage alertType="wellness">
                                        <WellnessComponent
                                            wellnessItems={showWellnessSampleData ? getSampleWellnessData() : wellnessData}
                                        />
                                    </NavigationPage>
                                )}
                            />
                        )
                    }
                    {
                        (hasBodyComposition || showBodyCompositionSampleData) && (
                            <Route
                                path="/body-composition"
                                element={(
                                    <NavigationPage alertType="bodyComposition">
                                        <BodyCompositionComponent
                                            bodyComposition={showBodyCompositionSampleData ? getSampleBodyComposition() : bodyComposition!}
                                        />
                                    </NavigationPage>
                                )}
                            />
                        )
                    }
                </Routes>
            </ThemeProvider>
        </Box>
    );
}

// force front end deploy
export default App;
